/**
 * Birim dönüşüm ve hesaplama yardımcı fonksiyonları
 * Ürünlerin gram/kilogram satışını destekler
 */

/**
 * Gram'dan kilogram'a dönüşüm
 * @param {number} gram - Gram cinsinden değer
 * @returns {number} Kilogram cinsinden değer
 */
export const gramToKg = (gram) => {
  return gram / 1000;
};

/**
 * Kilogram'dan gram'a dönüşüm
 * @param {number} kg - Kilogram cinsinden değer
 * @returns {number} Gram cinsinden değer
 */
export const kgToGram = (kg) => {
  return kg * 1000;
};

/**
 * Minimum değer kontrolü
 * @param {number} quantity - Miktar
 * @param {string} unit - Birim ('gram', 'kg', 'piece')
 * @param {number} minimumWeight - Minimum ağırlık (gram cinsinden)
 * @returns {boolean} Geçerli mi?
 */
export const isValidQuantity = (quantity, unit, minimumWeight) => {
  if (!minimumWeight || minimumWeight === 0) return true;
  
  let quantityInGrams = quantity;
  if (unit === 'kg') {
    quantityInGrams = kgToGram(quantity);
  }
  
  return quantityInGrams >= minimumWeight;
};

/**
 * Fiyat hesaplama (birim fiyat kg başına)
 * @param {number} pricePerKg - Kilogram başına fiyat
 * @param {number} quantity - Miktar
 * @param {string} unit - Birim ('gram', 'kg', 'piece')
 * @returns {number} Toplam fiyat
 */
export const calculatePrice = (pricePerKg, quantity, unit) => {
  if (unit === 'piece') {
    return pricePerKg * quantity;
  }
  
  let quantityInKg = quantity;
  if (unit === 'gram') {
    quantityInKg = gramToKg(quantity);
  }
  
  return pricePerKg * quantityInKg;
};

/**
 * Birim gösterim formatı
 * @param {number} saleType - Satış tipi (0: Adet, 1: Gram/Kg)
 * @param {number} quantity - Miktar
 * @param {string} unit - Birim ('gram', 'kg', 'piece')
 * @returns {string} Formatlanmış birim string
 */
export const formatUnit = (saleType, quantity, unit) => {
  if (saleType === 0) {
    return quantity === 1 ? 'Adet' : `${quantity} Adet`;
  }
  
  if (unit === 'kg') {
    return `${quantity} Kg`;
  }
  
  return `${quantity} gr`;
};

/**
 * Birim metni (tekil)
 * @param {number} saleType - Satış tipi (0: Adet, 1: Gram/Kg)
 * @param {string} unit - Birim ('gram', 'kg', 'piece')
 * @returns {string} Birim adı
 */
export const getUnitLabel = (saleType, unit) => {
  if (saleType === 0) return 'Adet';
  return unit === 'kg' ? 'Kg' : 'Gram';
};

/**
 * Otomatik birim seçimi (kullanıcı dostu değer için)
 * @param {number} gramValue - Gram cinsinden değer
 * @returns {object} { value, unit }
 */
export const autoSelectUnit = (gramValue) => {
  if (gramValue >= 1000 && gramValue % 1000 === 0) {
    return {
      value: gramToKg(gramValue),
      unit: 'kg'
    };
  }
  return {
    value: gramValue,
    unit: 'gram'
  };
};

/**
 * Miktar artırma (birime göre uygun adım)
 * @param {number} currentQuantity - Mevcut miktar
 * @param {string} unit - Birim
 * @param {number} step - Artış miktarı (opsiyonel)
 * @returns {number} Yeni miktar
 */
export const incrementQuantity = (currentQuantity, unit, step = null) => {
  const defaultStep = unit === 'kg' ? 0.25 : 250; // 0.25kg veya 250gr
  const actualStep = step || defaultStep;
  
  return Number((currentQuantity + actualStep).toFixed(unit === 'kg' ? 2 : 0));
};

/**
 * Miktar azaltma (birime göre uygun adım)
 * @param {number} currentQuantity - Mevcut miktar
 * @param {string} unit - Birim
 * @param {number} minimumValue - Minimum değer
 * @param {number} step - Azalış miktarı (opsiyonel)
 * @returns {number} Yeni miktar
 */
export const decrementQuantity = (currentQuantity, unit, minimumValue = 0, step = null) => {
  const defaultStep = unit === 'kg' ? 0.25 : 250; // 0.25kg veya 250gr
  const actualStep = step || defaultStep;
  
  const newQuantity = Number((currentQuantity - actualStep).toFixed(unit === 'kg' ? 2 : 0));
  
  return Math.max(newQuantity, minimumValue);
};

/**
 * Minimum miktar hesaplama (birime göre)
 * @param {number} minimumWeight - Minimum ağırlık (gram cinsinden)
 * @param {string} unit - Birim
 * @returns {number} Minimum miktar
 */
export const getMinimumQuantity = (minimumWeight, unit) => {
  if (!minimumWeight || minimumWeight === 0) {
    return unit === 'kg' ? 0.1 : 100;
  }
  
  if (unit === 'kg') {
    return gramToKg(minimumWeight);
  }
  
  return minimumWeight;
};

/**
 * Miktar validasyonu ve normalizasyonu
 * @param {number} quantity - Miktar
 * @param {string} unit - Birim
 * @param {number} minimumWeight - Minimum ağırlık (gram cinsinden)
 * @returns {object} { isValid, normalizedQuantity, errorMessage }
 */
export const validateAndNormalizeQuantity = (quantity, unit, minimumWeight) => {
  const numQuantity = Number(quantity);
  
  if (isNaN(numQuantity) || numQuantity <= 0) {
    return {
      isValid: false,
      normalizedQuantity: 0,
      errorMessage: 'Geçerli bir miktar giriniz'
    };
  }
  
  if (!isValidQuantity(numQuantity, unit, minimumWeight)) {
    const minQty = getMinimumQuantity(minimumWeight, unit);
    return {
      isValid: false,
      normalizedQuantity: minQty,
      errorMessage: `Minimum sipariş miktarı ${minQty} ${getUnitLabel(1, unit)}`
    };
  }
  
  return {
    isValid: true,
    normalizedQuantity: numQuantity,
    errorMessage: null
  };
};

/**
 * Backend için miktar normalizasyonu (her zaman gram cinsinden gönder)
 * @param {number} quantity - Miktar
 * @param {string} unit - Birim
 * @param {number} saleType - Satış tipi (0: Adet, 1: Gram/Kg)
 * @returns {number} Normalize edilmiş miktar
 */
export const normalizeQuantityForBackend = (quantity, unit, saleType) => {
  if (saleType === 0) {
    return quantity; // Adet bazlı
  }
  
  if (unit === 'kg') {
    return kgToGram(quantity); // Kg'yi gram'a çevir
  }
  
  return quantity; // Zaten gram cinsinden
};

/**
 * Fiyat gösterimi formatı
 * @param {number} price - Fiyat
 * @param {number} saleType - Satış tipi
 * @returns {string} Formatlanmış fiyat string
 */
export const formatPriceWithUnit = (price, saleType) => {
  const formattedPrice = price.toFixed(2);
  const unitText = saleType === 1 ? '/Kg' : '/Adet';
  
  return `₺${formattedPrice} ${unitText}`;
};

/**
 * Akıllı miktar gösterimi - gram değerini kullanıcı dostu formata çevirir
 * @param {number} gramValue - Gram cinsinden değer
 * @returns {object} { value, unit, displayText }
 */
export const formatQuantityDisplay = (gramValue) => {
  if (gramValue >= 1000) {
    const kgValue = gramValue / 1000;
    // Yuvarlama yapmadan, gereksiz sıfırları kaldır
    const formattedValue = kgValue % 1 === 0 ? kgValue.toString() : kgValue.toFixed(2).replace(/\.?0+$/, '');
    return { 
      value: kgValue, 
      unit: 'kg', 
      displayText: `${formattedValue} Kg` 
    };
  }
  return { 
    value: gramValue, 
    unit: 'gram', 
    displayText: `${gramValue} Gram` 
  };
};

/**
 * Gram değerine göre birim metni döndürür
 * @param {number} gramValue - Gram cinsinden değer
 * @returns {string} 'Kg' veya 'Gram'
 */
export const getDisplayUnit = (gramValue) => {
  if (gramValue >= 1000) {
    return 'Kg';
  }
  return 'Gram';
};

/**
 * Gram değerini tam formatlı string olarak döndürür
 * @param {number} gramValue - Gram cinsinden değer
 * @returns {string} "1 Kg" veya "750 Gram"
 */
export const formatQuantityWithUnit = (gramValue) => {
  const display = formatQuantityDisplay(gramValue);
  return display.displayText;
};

